import { Order } from '@/coreDomainsModule/entities/order';
import { ReminderMailInfo } from '@/remindModule/entities/reminder';
import { GetReminderDataUseCase } from './getReminderDataUseCase';
import { diContainer } from '@/coreContainer/container';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * 発注情報のreminderMailDataIdをクリーニングするUseCase
 */
export class CleanupOrderReminderMailIdUseCase {
  private getReminderDataUseCase: GetReminderDataUseCase;

  /**
   * コンストラクタ
   */
  constructor() {
    this.getReminderDataUseCase = new GetReminderDataUseCase();
  }

  /**
   * 発注リストのreminderMailDataIdをクリーニングする
   * @param orders - クリーニング対象の発注リスト
   * @param reminderList - 現在のリマインダー情報リスト
   * @returns クリーニングされた発注リストと更新が必要な発注IDのリスト
   */
  async execute(
    orders: Order[],
    reminderList: ReminderMailInfo[]
  ): Promise<{
    cleanedOrders: Order[];
    ordersToUpdate: string[];
  }> {
    const cleanedOrders: Order[] = [];
    const ordersToUpdate: string[] = [];
    const bulkUpdates: Array<{ orderId: string; reminderMailDataId: string | null; updateAt: number }> = [];

    for (const order of orders) {
      const cleanedOrder = { ...order };
      let needsUpdate = false;

      // reminderMailDataIdが存在する場合のみチェック
      if (cleanedOrder.reminderMailDataId) {
        const reminder = this.getReminderDataUseCase.findReminderById(cleanedOrder.reminderMailDataId, reminderList);

        // リマインダーが見つからない、またはリマインダー日付が本日より過去の場合はクリア
        // 日付はYYYYMMDD形式のため、数値比較で判定する
        const isPastReminder = reminder ? Number(reminder.date) < Number(DateService.getTodayString()) : false;
        if (!reminder || isPastReminder) {
          cleanedOrder.reminderMailDataId = null;
          needsUpdate = true;
          ordersToUpdate.push(order.orderId);

          // 一括更新用の配列に追加（空文字でクリア）
          bulkUpdates.push({
            orderId: order.orderId,
            reminderMailDataId: '',
            updateAt: order.updatedAt,
          });
        }
      }

      cleanedOrders.push(cleanedOrder);
    }

    // 一括でバックエンドの発注情報を更新
    if (bulkUpdates.length > 0) {
      await this.updateOrderReminderMailIdBulk(bulkUpdates);
    }

    return {
      cleanedOrders,
      ordersToUpdate,
    };
  }

  /**
   * 単一の発注のreminderMailDataIdをクリーニングする
   * @param order - クリーニング対象の発注
   * @param reminderList - 現在のリマインダー情報リスト
   * @returns クリーニング済みの発注と更新が必要かどうかのフラグ
   */
  async cleanupSingleOrder(
    order: Order,
    reminderList: ReminderMailInfo[]
  ): Promise<{
    cleanedOrder: Order;
    needsUpdate: boolean;
  }> {
    const cleanedOrder = { ...order };
    let needsUpdate = false;

    // reminderMailDataIdが存在する場合のみチェック
    if (cleanedOrder.reminderMailDataId) {
      const reminder = this.getReminderDataUseCase.findReminderById(cleanedOrder.reminderMailDataId, reminderList);

      // リマインダーが見つからない、またはリマインダー日付が本日より過去の場合はreminderMailDataIdをクリア
      const isPastReminder = reminder ? Number(reminder.date) < Number(DateService.getTodayString()) : false;
      if (!reminder || isPastReminder) {
        cleanedOrder.reminderMailDataId = null;
        needsUpdate = true;

        // バックエンドの発注情報も更新
        await this.updateOrderReminderMailId(order.orderId, '', order.updatedAt);
      }
    }

    return {
      cleanedOrder,
      needsUpdate,
    };
  }

  /**
   * バックエンドの発注情報のreminderMailDataIdを一括更新する
   * @param updates - 更新情報の配列
   */
  private async updateOrderReminderMailIdBulk(
    updates: Array<{ orderId: string; reminderMailDataId: string | null; updateAt: number }>
  ): Promise<void> {
    try {
      await diContainer.updateOrderReminderMailIdUseCase.ExecuteBulk(updates);
    } catch (error) {
      console.error('一括でのreminderMailDataId更新に失敗しました:', error);
      // エラーが発生してもクリーニング処理は続行する
    }
  }

  /**
   * バックエンドの発注情報のreminderMailDataIdを更新する
   * @param orderId - 発注ID
   * @param reminderMailDataId - 更新するreminderMailDataId（nullの場合はクリア）
   * @param updateAt - 更新時刻
   */
  private async updateOrderReminderMailId(orderId: string, reminderMailDataId: string | null, updateAt: number): Promise<void> {
    try {
      await diContainer.updateOrderReminderMailIdUseCase.Execute(orderId, reminderMailDataId, updateAt);
    } catch (error) {
      console.error(`発注ID ${orderId} のreminderMailDataId更新に失敗しました:`, error);
      // エラーが発生してもクリーニング処理は続行する
    }
  }
}
